import React, { useState } from 'react';
import { Box, Button, IconButton, Popover, TextField, Tooltip, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import { getMonday, formatDateISO, addDays, formatWeekLabel, parseISODate, isCurrentWeek } from '../utils/weekUtils';

const T = {
  primary: '#526075',
  text: '#2d3335',
  font: '"Manrope", system-ui, sans-serif',
};

/**
 * Weeknavigatie met datumprikker: ‹ [Week 33 · 10 – 16 aug] ›
 *
 * Feedback uit de wiki was dat je alleen kon doorklikken naar een week — je moest dus
 * door tien weken heen scrollen om een datum in oktober te zien. Klikken op het label
 * opent een agenda; de gekozen datum springt naar de maandag van díe week.
 *
 * `onChange` krijgt altijd een maandag (Date, lokale middernacht).
 */
export default function WeekSelector({
  weekStart,
  onChange,
  rangeWeeks = 1,
  size = 'small',
  minWidth = 200,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  // Concept-datum: het native datumveld vuurt onChange bij élke toetsaanslag (dag typen =
  // al een geldige datum), waardoor de week wegsprong voordat je klaar was met typen
  // (feedback Raijmond 10 sep). Toepassen gebeurt nu pas bij Enter, blur of de knop.
  const [concept, setConcept] = useState('');

  const stap = (richting) => onChange(addDays(getMonday(weekStart), richting * 7 * rangeWeeks));

  const kiesDatum = (waarde) => {
    const datum = parseISODate(waarde);
    if (!datum) return;
    onChange(getMonday(datum));
    setConcept('');
    setAnchorEl(null);
  };

  const naarDezeWeek = () => {
    onChange(getMonday(new Date()));
    setAnchorEl(null);
  };

  const opDezeWeek = isCurrentWeek(weekStart);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title={rangeWeeks > 1 ? `${rangeWeeks} weken terug` : 'Vorige week'}>
        <IconButton onClick={() => stap(-1)} size={size}><ChevronLeftIcon /></IconButton>
      </Tooltip>

      <Tooltip title="Kies een week">
        <Button
          variant="outlined"
          size={size}
          startIcon={<TodayIcon />}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontFamily: T.font,
            borderRadius: 2,
            minWidth,
            color: opDezeWeek ? T.primary : T.text,
          }}
        >
          {formatWeekLabel(weekStart, rangeWeeks)}
        </Button>
      </Tooltip>

      <Tooltip title={rangeWeeks > 1 ? `${rangeWeeks} weken vooruit` : 'Volgende week'}>
        <IconButton onClick={() => stap(1)} size={size}><ChevronRightIcon /></IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => { setConcept(''); setAnchorEl(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 240 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: T.font }}>
            Spring naar een datum
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              type="date"
              size="small"
              autoFocus
              value={concept || formatDateISO(getMonday(weekStart))}
              onChange={(e) => setConcept(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); kiesDatum(concept || e.target.value); } }}
              onBlur={(e) => { if (concept && concept !== formatDateISO(getMonday(weekStart))) kiesDatum(e.target.value); }}
              inputProps={{ 'aria-label': 'Datum kiezen' }}
            />
            <Button size="small" variant="contained" disableElevation onClick={() => kiesDatum(concept || formatDateISO(getMonday(weekStart)))}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
              Ga
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Je springt naar de week waarin die datum valt.
          </Typography>
          <Button onClick={naarDezeWeek} size="small" sx={{ textTransform: 'none', alignSelf: 'flex-start' }}>
            Deze week
          </Button>
        </Box>
      </Popover>
    </Box>
  );
}
